// Statement downloads (§11.2): CSV and PDF built client-side from the
// ledger entries, so both buttons produce real files without a server.
import type { StatementEntry } from '@/types/phase2.types';

const dayLabel = (ts: number): string =>
  new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const signed = (amountRupees: number): string =>
  `${amountRupees >= 0 ? '+' : '-'}${Math.abs(amountRupees).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// ---------- CSV ----------

export const buildStatementCsv = (entries: StatementEntry[]): Blob => {
  const esc = (value: string): string => `"${value.replace(/"/g, '""')}"`;
  const rows = [
    'Date,Description,Counterparty,Category,Reference,Amount (INR)',
    ...entries.map((e) =>
      [
        dayLabel(e.date),
        esc(e.description),
        esc(e.counterparty ?? ''),
        esc(e.category),
        esc(e.reference ?? ''),
        signed(e.amountRupees),
      ].join(',')
    ),
  ];
  return new Blob([rows.join('\r\n')], { type: 'text/csv;charset=utf-8' });
};

// ---------- PDF ----------

/** WinAnsi-safe text: the built-in Helvetica font has no ₹ glyph. */
const pdfSafe = (text: string): string =>
  text
    .replace(/₹/g, 'Rs ')
     
    .replace(/[^\x20-\x7E\xA0-\xFF]/g, ' ')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');

const LINES_PER_PAGE = 42;
const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;

const statementLines = (entries: StatementEntry[], periodLabel: string): Array<{ text: string; size: number }> => {
  const lines: Array<{ text: string; size: number }> = [
    { text: 'NovaBank - Account statement', size: 16 },
    { text: `Period: ${periodLabel}`, size: 10 },
    { text: `Generated: ${new Date().toLocaleString('en-IN')}`, size: 10 },
    { text: '', size: 10 },
  ];
  for (const e of entries) {
    const where = e.counterparty ? ` - ${e.counterparty}` : '';
    lines.push({ text: `${dayLabel(e.date)}  ${e.description}${where}`.slice(0, 95), size: 10 });
    const ref = e.reference ? `  Ref ${e.reference}` : '';
    lines.push({ text: `   ${e.category}${ref}   Rs ${signed(e.amountRupees)}`.slice(0, 95), size: 9 });
  }
  if (entries.length === 0) lines.push({ text: 'No transactions in this period.', size: 10 });
  return lines;
};

export const buildStatementPdf = (entries: StatementEntry[], periodLabel: string): Blob => {
  const lines = statementLines(entries, periodLabel);
  const pages: Array<Array<{ text: string; size: number }>> = [];
  for (let i = 0; i < lines.length; i += LINES_PER_PAGE) {
    pages.push(lines.slice(i, i + LINES_PER_PAGE));
  }

  // Object numbers: 1 catalog, 2 pages, 3 font, then per page (page, content).
  const objects: string[] = [];
  const pageIds = pages.map((_, i) => 4 + i * 2);
  objects[0] = '<< /Type /Catalog /Pages 2 0 R >>';
  objects[1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`;
  objects[2] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>';

  pages.forEach((pageLines, i) => {
    const pageId = 4 + i * 2;
    const contentId = pageId + 1;
    let y = PAGE_HEIGHT - 60;
    const body = pageLines
      .map((line) => {
        const out = `BT /F1 ${line.size} Tf 50 ${y} Td (${pdfSafe(line.text)}) Tj ET`;
        y -= line.size > 12 ? 26 : 17;
        return out;
      })
      .join('\n');
    objects[pageId - 1] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] ` +
      `/Resources << /Font << /F1 3 0 R >> >> /Contents ${contentId} 0 R >>`;
    objects[contentId - 1] = `<< /Length ${body.length} >>\nstream\n${body}\nendstream`;
  });

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((body, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets) {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
};
